import React, { useCallback } from "react";
import { useActiveLeague } from "../../hooks/useActiveLeague";
import { useCurrentTeamId } from "../../hooks/useCurrentTeamId";
import Flag from "../common/Flag";
import { Link } from "react-router-dom";
import CountdownTimer from "../common/CountdownTimer";
import TutorialTooltip from '../common/TutorialTooltip';
import { isActiveTransferBidForPlayer } from "../../utils/transferBidStatus.js";

import { numToPrice, sortArrayByKey, getColumnClass } from "../utils";

const PlayersBlock = React.memo(function PlayersBlock({ 
  players = [], 
  showFields, 
  unsignable = false, 
  handlePopup, 
  setSelectedPlayer,
  onBidPlayer,
  userBids = [] // Array of user's existing bids
}) {
  const { activeLeague } = useActiveLeague();
  const isQuickplayLeague = activeLeague?.gameMode === 'quickplay';
  const bidWindowLabel = isQuickplayLeague ? '5 minutes' : '1 hour';
  const bidWindowVerb = isQuickplayLeague ? 'have' : 'has';
  const currentTeamId = useCurrentTeamId();
  const [sortBy, setSortBy] = React.useState("id");
  const [sortType, setSortType] = React.useState("desc");

  const visiblePlayers = players.filter((item) => item.currentTeam?.id !== currentTeamId);
  const sortedPlayers = sortArrayByKey([...visiblePlayers], sortBy, sortType);

  function handleSort(key) {
    if (sortBy === key) {
      setSortType(sortType === "asc" ? "desc" : "asc");
    } else {
      setSortType("desc");
      setSortBy(key);
    }
  }

  const handlePlayerSelect = async (player, event) => {
    console.log('=== BID BUTTON CLICKED ===');
    console.log('Player:', player.name);
    console.log('Event:', event);
    console.log('Event target:', event.target);
    
    // Prevent event from bubbling up to the row click handler
    event.preventDefault();
    event.stopPropagation();
    
    if (onBidPlayer) {
      console.log('Opening bid popup via parent handler...');
      await onBidPlayer(player);
    } else {
      if (setSelectedPlayer) {
        console.log('Setting selected player...');
        setSelectedPlayer(player);
      }
      if (handlePopup) {
        console.log('Calling handlePopup with bid...');
        handlePopup('bid');
      }
    }
    
    console.log('=== BID BUTTON CLICK COMPLETE ===');
  };

  const handleRowClick = useCallback((wyId, event) => {
    const clickedElement = event.target.closest('.bid-btn, .unsign-btn, .ico, button, a');
    if (clickedElement) {
      return;
    }
    
    const urlBase = activeLeague?.slug ? `/${activeLeague.slug}` : '';
    const leagueQuery = activeLeague?._id ? `?leagueId=${activeLeague._id}` : '';
    window.open(`${urlBase}/player/${wyId}${leagueQuery}`, '_blank');
  }, []);

  const offerItems = sortedPlayers.map(function (item, index) {
    const unsignButton = () => {
      if (unsignable) {
        return (
          <button
            className="unsign-btn" // Keeping existing class, can be styled or renamed later
            type="button"
            onClick={(event) => {
              event.preventDefault();
              event.stopPropagation();
              handlePopup('drop', item); // 'item' is the player object from the map's scope
            }}
            title="Drop Player"
            aria-label="Drop Player"
          >
            DROP
          </button>
        );
      }
      return "";
    };

    // Only active bids should block another offer. Outbid/refunded rows are history.
    const existingBid = userBids.find(bid => isActiveTransferBidForPlayer(bid, item.wyId));
    const hasBid = !!existingBid;
    return (
      <div 
        key={item.wyId} 
        className="row pos-col clickable-row"
        onClick={(e) => handleRowClick(item.wyId, e)}
      >
        {Object.entries(showFields).map(([key, value]) => {
          const itemVal = () => {
            if (key === "value" || key === "signedFor" || key === "myOffer") {
              return numToPrice(item[key], "€");
            }
            if (key === "timeRemaining") {
              return <CountdownTimer expiresAt={item[key]} className="bid-timer" />;
            }
            if (key === "nationality") {
              // Handle dual nationality display (birth + passport)
              const birthCode = item.birthNationalityCode;
              const passportCode = item.passportNationalityCode;
              
              // If both exist and are different, show both flags
              if (item.birthNationality && item.passportNationality && 
                  item.birthNationalityCode !== item.passportNationalityCode) {
                return (
                  <div className="image dual-nationality">
                    <Flag code={birthCode} fallback={item.birthNationality} />
                    <Flag code={passportCode} fallback={item.passportNationality} />
                  </div>
                );
              }
              // If only one nationality or both are the same, show single flag
              else if (item.birthNationality || item.passportNationality) {
                const flagCode = birthCode || passportCode;
                const nationality = item.birthNationality || item.passportNationality;
                return (
                  <div className="image">
                    <Flag code={flagCode} fallback={nationality} />
                  </div>
                );
              }
              // Fallback to legacy nationality field if new fields don't exist
              else {
                const flagCode = item.nationalityCode || item[key];
                return (
                  <div className="image">
                    <Flag code={flagCode} fallback={item[key]} />
                  </div>
                );
              }
            }
            if (key === "height" && item[key]) {
              return (
                <span>
                  {item[key]}
                  <span className="unit"> cm</span>
                </span>
              );
            }
            if (key === "weight" && item[key]) {
              return (
                <span>
                  {item[key]}
                  <span className="unit"> kg</span>
                </span>
              );
            }
            if (key === "status") {
              const statusText = String(item[key] || 'pending');
              const normalizedStatus = statusText.toLowerCase();
              const statusClass = normalizedStatus === 'winning' ? 'status-winning' :
                                normalizedStatus === 'losing' ? 'status-losing' :
                                normalizedStatus === 'outbid' ? 'status-outbid' :
                                'status-pending';
              return (
                <span className={`bid-status ${statusClass}`}>
                  {statusText}
                </span>
              );
            }
            if (key === "currentTeam" || key === "ownership") {
              if (item.isFreeAgent) {
                return (
                  <span className="ownership-status free-agent">
                    Free Agent
                  </span>
                );
              } else if (item.currentTeam) {
                return (
                  <span className="ownership-status owned">
                    {item.currentTeam.name || `Team ${item.currentTeam.id.slice(-4)}`}
                  </span>
                );
              } else {
                return (
                  <span className="ownership-status unknown">
                    Unknown
                  </span>
                );
              }
            }
            if (key === "age") {
              return item[key] !== null && typeof item[key] !== 'undefined' ? item[key] : "N/A";
            }
            return item[key];
          };

          return (
            <div key={key} className={`col ${getColumnClass(key)}`}>
              {itemVal()}
              {key === "name" && !unsignable && (
                <button
                  className={`bid-btn mobile-btn ${hasBid ? 'bid-made' : ''}`}
                  style={{ pointerEvents: 'auto', zIndex: 100 }} // Force pointer events and higher z-index
                  type="button"
                  disabled={hasBid}
                  onClick={(event) => {
                    if (!hasBid) {
                      console.log('Button raw click detected');
                      handlePlayerSelect(item, event);
                    }
                  }}
                  onMouseDown={(e) => console.log('Button mouse down')}
                  onMouseUp={(e) => console.log('Button mouse up')}
                >
                  {hasBid ? 'BID MADE' : 'BID'}
                </button>
              )}
            </div>
          );
        })}

        <div key={"ctrls-column"} className={`col ctrls-col ${!unsignable ? 'thin-ctrls' : ''}`}>
          <Link to={`${activeLeague?.slug ? `/${activeLeague.slug}` : ''}/player/${item.wyId}${activeLeague?._id ? `?leagueId=${activeLeague._id}` : ''}`} className="ico icon-tip" />
          {unsignButton()}
          {!unsignable && (
            <button
              className={`bid-btn desktop-btn ${hasBid ? 'bid-made' : ''}`}
              style={{ pointerEvents: 'auto', zIndex: 100 }} // Force pointer events and higher z-index
              type="button"
              disabled={hasBid}
              onClick={(event) => {
                if (!hasBid) {
                  console.log('Button raw click detected');
                  handlePlayerSelect(item, event);
                }
              }}
              onMouseDown={(e) => console.log('Button mouse down')}
              onMouseUp={(e) => console.log('Button mouse up')}
            >
              {hasBid ? 'BID MADE' : 'BID'}
            </button>
          )}
        </div>
      </div>
    );
  });

  return (
    <div className="players-list">
      <div className="row head-row">
        {Object.entries(showFields).map(([key, value], index) => (
          <div key={index} className={`col ${getColumnClass(key)}`}>
            <button
              type="button"
              className={`sort-btn ${key === sortBy ? sortType : ""}`}
              onClick={() => {
                handleSort(key);
              }}
            >
              {value}
            </button>
            {key === "value" && (
              <TutorialTooltip
                id="players-bids-tooltip"
                title="Player bids"
                description={`Player bids work like an auction: after a bid is made, others have ${bidWindowLabel} to beat it. After ${bidWindowLabel} ${bidWindowVerb} passed, the player is sold to the highest bidder.`}
                className="f-left"
              />
            )}
          </div>
        ))}
        <div key={"ctrls-column"} className={`col ctrls-col ${!unsignable ? 'thin-ctrls' : ''}`}>
          {unsignable && (
            <TutorialTooltip
              id="drop-player-tooltip"
              title="Drop Player"
              description="Click here to remove a player from your team. The player’s market value will be returned to your transfer budget."
              className="f-right"
            />
          )}
        </div>
      </div>

      {offerItems}
    </div>
  );
});

export { PlayersBlock };
